import { useEffect, useRef, useState } from "react";
import { ICurrency } from "../models/IExpense";
import { IExpenseVendor } from "../models/IExpenseVendor";
import { createExpense, getPlatformCurrencyList } from "../services/expense";
import { getUserListByRoleIds } from "../services/auth";
import { SUPER_ADMIN_ROLE_ID } from "../services/checkRole";
import { IOnCallUserOption } from "../live-chat-module/models/IOnCallAgent";
import VendorManagerModal from "./VendorManagerModal";
import VendorPicker from "./VendorPicker";

const MAX_FILE_BYTES = 2 * 1024 * 1024;

interface Props {
  myUserId: string;
  myDisplayName: string;
  onClose: () => void;
  onCreated: () => void;
}

function UserPicker({ onSelect, onClose }: { onSelect: (u: IOnCallUserOption) => void; onClose: () => void }) {
  const [users, setUsers] = useState<IOnCallUserOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    getUserListByRoleIds([SUPER_ADMIN_ROLE_ID])
      .then((res) =>
        setUsers((res.data ?? []).map((u) => ({
          id: u.id,
          displayName: `${u.customerName} ${u.customerSurName}`.trim(),
          phoneNumber: u.customerTelNo ?? "",
        })))
      )
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const filtered = users.filter((u) =>
    `${u.displayName} ${u.phoneNumber}`.toLowerCase().includes(search.toLowerCase())
  );

  const getInitials = (name: string) =>
    name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2) || "?";

  return (
    <div style={{ position: "absolute", inset: 0, zIndex: 10, background: "#fff", display: "flex", flexDirection: "column", borderRadius: "inherit" }}>
      <div style={{ padding: "12px 16px", borderBottom: "1.5px solid #f1f5f9", display: "flex", alignItems: "center", gap: 10, flexShrink: 0, background: "#fafafa" }}>
        <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280", fontSize: "1rem", padding: 0 }}>
          <i className="bi bi-arrow-left" />
        </button>
        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Kullanıcı ara…"
          style={{ flex: 1, borderRadius: 9, border: "1.5px solid #e0e7ff", padding: "7px 12px", fontSize: "0.88rem", outline: "none", background: "#f8faff", fontFamily: "inherit" }}
        />
      </div>
      <div style={{ flex: 1, overflowY: "auto" }}>
        {loading && <div style={{ display: "flex", justifyContent: "center", padding: "32px 0" }}><span className="spinner-border text-primary" /></div>}
        {!loading && filtered.length === 0 && <div style={{ textAlign: "center", padding: "32px 16px", color: "#9ca3af", fontSize: "0.84rem" }}>Kullanıcı bulunamadı</div>}
        {filtered.map((u) => (
          <button key={u.id} onClick={() => onSelect(u)}
            style={{ width: "100%", textAlign: "left", border: "none", borderBottom: "1px solid #f1f5f9", background: "transparent", padding: "11px 16px", cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: 9, flexShrink: 0, background: "linear-gradient(135deg,#6366f1,#4f46e5)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "0.72rem", color: "#fff" }}>
              {getInitials(u.displayName)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: "0.86rem", color: "#111827" }}>{u.displayName}</div>
              {u.phoneNumber && <div style={{ fontSize: "0.72rem", color: "#9ca3af" }}>{u.phoneNumber}</div>}
            </div>
            <i className="bi bi-chevron-right" style={{ color: "#d1d5db", fontSize: "0.7rem" }} />
          </button>
        ))}
      </div>
    </div>
  );
}

export default function AddExpenseModal({ myUserId, myDisplayName, onClose, onCreated }: Props) {
  const [currencies, setCurrencies] = useState<ICurrency[]>([]);
  const [currLoading, setCurrLoading] = useState(true);

  const [selectedVendor, setSelectedVendor] = useState<IExpenseVendor | null>(null);
  const [showVendorPicker, setShowVendorPicker] = useState(false);
  const [showVendorManager, setShowVendorManager] = useState(false);

  const [selectedUser, setSelectedUser] = useState<{ id: string; name: string } | null>(null);
  const [amount, setAmount] = useState("");
  const [currencyId, setCurrencyId] = useState("");
  const [spentAt, setSpentAt] = useState(() => new Date().toISOString().slice(0, 16));
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPicker, setShowPicker] = useState(false);

  const [isNarrow, setIsNarrow] = useState(window.innerWidth < 640);
  useEffect(() => {
    const h = () => setIsNarrow(window.innerWidth < 640);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  useEffect(() => {
    getPlatformCurrencyList()
      .then((res) => {
        setCurrencies(res.data);
        if (res.data.length > 0) setCurrencyId(res.data[0].id);
      })
      .catch(() => {})
      .finally(() => setCurrLoading(false));
  }, []);

  const handleFiles = (incoming: FileList | null) => {
    if (!incoming) return;
    const arr = Array.from(incoming);
    const tooBig = arr.find((f) => f.size > MAX_FILE_BYTES);
    if (tooBig) { setFileError(`"${tooBig.name}" 2 MB sınırını aşıyor.`); return; }
    setFileError(null);
    setFiles((prev) => [...prev, ...arr]);
  };

  const removeFile = (idx: number) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    if (!selectedUser) { setError("Kullanıcı seçiniz."); return; }
    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) { setError("Geçerli bir tutar giriniz."); return; }
    if (!currencyId) { setError("Para birimi seçiniz."); return; }
    if (!description.trim()) { setError("Açıklama zorunludur."); return; }
    setError(null);
    setSaving(true);
    try {
      await createExpense({
        spentByUserId: selectedUser.id,
        amount: Number(amount),
        currencyId,
        spentAt: new Date(spentAt).toISOString(),
        description: description.trim(),
        vendorId: selectedVendor?.id,
        documents: files.length > 0 ? files : undefined,
      });
      onCreated();
      onClose();
    } catch {
      setError("Fatura oluşturulamadı. Lütfen tekrar deneyin.");
    } finally {
      setSaving(false);
    }
  };

  const panelStyle: React.CSSProperties = isNarrow
    ? { position: "fixed", inset: 0, zIndex: 2040, background: "#fff", display: "flex", flexDirection: "column", overflow: "hidden" }
    : { position: "fixed", top: "50%", left: "50%", zIndex: 2040, transform: "translate(-50%,-50%)", width: "min(560px,calc(100vw - 24px))", maxHeight: "90dvh", background: "#fff", borderRadius: 20, boxShadow: "0 24px 64px rgba(0,0,0,0.18)", display: "flex", flexDirection: "column", overflow: "hidden" };

  const inp: React.CSSProperties = { width: "100%", borderRadius: 10, border: "1.5px solid #e0e7ff", padding: "9px 13px", fontSize: "0.88rem", color: "#111827", outline: "none", background: "#f8faff", fontFamily: "inherit", boxSizing: "border-box" };

  return (
    <>
      {!isNarrow && <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 2039, background: "rgba(0,0,0,0.45)", backdropFilter: "blur(3px)" }} />}
      <div style={panelStyle}>
        {/* Header */}
        <div style={{ padding: isNarrow ? "14px 16px" : "16px 20px", background: "linear-gradient(135deg,#1d4ed8,#2563eb)", display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, background: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <i className="bi bi-receipt-cutoff text-white" style={{ fontSize: "1rem" }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: "0.96rem", color: "#fff" }}>Şahıs Faturası Ekle</div>
            <div style={{ fontSize: "0.66rem", color: "rgba(255,255,255,0.65)" }}>Yeni harcama kaydı oluştur</div>
          </div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 9, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff", fontSize: "0.9rem" }}>
            <i className="bi bi-x-lg" />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", position: "relative" }}>
          {showPicker && <UserPicker onSelect={(u) => { setSelectedUser({ id: u.id, name: u.displayName }); setShowPicker(false); }} onClose={() => setShowPicker(false)} />}
          {showVendorPicker && <VendorPicker onSelect={(v) => { setSelectedVendor(v); setShowVendorPicker(false); }} onClose={() => setShowVendorPicker(false)} />}

          <div style={{ padding: isNarrow ? "14px" : "20px", display: "flex", flexDirection: "column", gap: 14 }}>
            {/* Kullanıcı */}
            <div>
              <label style={{ fontSize: "0.68rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 6 }}>Harcayan *</label>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <div style={{ flex: "1 1 180px", ...inp, display: "flex", alignItems: "center", gap: 8, color: selectedUser ? "#111827" : "#9ca3af", cursor: "default" }}>
                  {selectedUser
                    ? <><i className="bi bi-person-check-fill" style={{ color: "#6366f1" }} />{selectedUser.name}</>
                    : <><i className="bi bi-person" />Seçilmedi</>
                  }
                </div>
                <button onClick={() => setSelectedUser({ id: myUserId, name: myDisplayName })}
                  style={{ flex: "0 0 auto", background: "#eef2ff", border: "1.5px solid #c7d2fe", borderRadius: 10, padding: "9px 12px", fontWeight: 700, fontSize: "0.78rem", color: "#4f46e5", cursor: "pointer", whiteSpace: "nowrap" }}>
                  <i className="bi bi-person-check me-1" />Ben
                </button>
                <button onClick={() => setShowPicker(true)}
                  style={{ flex: "0 0 auto", background: "#f9fafb", border: "1.5px solid #e5e7eb", borderRadius: 10, padding: "9px 12px", fontWeight: 700, fontSize: "0.78rem", color: "#374151", cursor: "pointer", whiteSpace: "nowrap" }}>
                  <i className="bi bi-people me-1" />Listeden Seç
                </button>
              </div>
            </div>

            {/* Tutar + Para birimi */}
            <div style={{ display: "flex", gap: 10 }}>
              <div style={{ flex: 2 }}>
                <label style={{ fontSize: "0.68rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 6 }}>Tutar *</label>
                <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" style={inp} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: "0.68rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 6 }}>Para Birimi *</label>
                {currLoading
                  ? <div style={{ ...inp, color: "#9ca3af" }}>Yükleniyor…</div>
                  : (
                    <select value={currencyId} onChange={(e) => setCurrencyId(e.target.value)} style={{ ...inp, cursor: "pointer" }}>
                      {currencies.map((c) => <option key={c.id} value={c.id}>{c.currencyDisplayText} — {c.currencyName}</option>)}
                    </select>
                  )
                }
              </div>
            </div>

            {/* Tarih */}
            <div>
              <label style={{ fontSize: "0.68rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 6 }}>Harcama Tarihi</label>
              <input type="datetime-local" value={spentAt} onChange={(e) => setSpentAt(e.target.value)} style={inp} />
            </div>

            {/* Firma */}
            <div>
              <label style={{ fontSize: "0.68rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 6 }}>
                Firma <span style={{ fontWeight: 400, textTransform: "none" }}>(opsiyonel)</span>
              </label>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <div style={{ flex: "1 1 180px", ...inp, display: "flex", alignItems: "center", gap: 8, color: selectedVendor ? "#111827" : "#9ca3af", cursor: "default" }}>
                  {selectedVendor
                    ? <><i className="bi bi-building-fill" style={{ color: "#b45309" }} />{selectedVendor.name}</>
                    : <><i className="bi bi-building" />Seçilmedi</>
                  }
                  {selectedVendor && (
                    <button type="button" onClick={() => setSelectedVendor(null)} title="Kaldır"
                      style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "#9ca3af", padding: 0, display: "flex" }}>
                      <i className="bi bi-x-circle-fill" />
                    </button>
                  )}
                </div>
                <button type="button" onClick={() => setShowVendorPicker(true)}
                  style={{ flex: "0 0 auto", background: "#f9fafb", border: "1.5px solid #e5e7eb", borderRadius: 10, padding: "9px 12px", fontWeight: 700, fontSize: "0.78rem", color: "#374151", cursor: "pointer", whiteSpace: "nowrap" }}>
                  <i className="bi bi-search me-1" />{selectedVendor ? "Değiştir" : "Listeden Seç"}
                </button>
                <button type="button" onClick={() => setShowVendorManager(true)} title="Firmaları yönet"
                  style={{ flex: "0 0 auto", background: "#fffbeb", border: "1.5px solid #fde68a", borderRadius: 10, padding: "9px 12px", fontWeight: 700, fontSize: "0.78rem", color: "#b45309", cursor: "pointer", whiteSpace: "nowrap" }}>
                  <i className="bi bi-building-add me-1" />Firmalar
                </button>
              </div>
            </div>

            {/* Açıklama */}
            <div>
              <label style={{ fontSize: "0.68rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 6 }}>Açıklama *</label>
              <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Harcama açıklaması…" style={{ ...inp, resize: "vertical" }} />
            </div>

            {/* Dökümanlar */}
            <div>
              <label style={{ fontSize: "0.68rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 6 }}>
                Belgeler <span style={{ fontWeight: 400, textTransform: "none" }}>(opsiyonel, maks 2 MB)</span>
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 8, border: "1.5px dashed #c7d2fe", borderRadius: 10, padding: "10px 14px", cursor: "pointer", background: "#f8faff" }}>
                <i className="bi bi-cloud-upload" style={{ color: "#6366f1", fontSize: "1.1rem" }} />
                <span style={{ fontSize: "0.82rem", color: "#6b7280" }}>Dosya seç veya sürükle bırak</span>
                <input type="file" multiple accept="image/*,.pdf,.doc,.docx" style={{ display: "none" }} onChange={(e) => handleFiles(e.target.files)} />
              </label>
              {fileError && <div style={{ marginTop: 6, fontSize: "0.76rem", color: "#dc2626" }}>{fileError}</div>}
              {files.length > 0 && (
                <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
                  {files.map((f, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, background: "#f1f5f9", borderRadius: 8, padding: "5px 10px", fontSize: "0.78rem" }}>
                      <i className="bi bi-file-earmark" style={{ color: "#6366f1" }} />
                      <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
                      <span style={{ color: "#9ca3af", flexShrink: 0 }}>{(f.size / 1024).toFixed(0)} KB</span>
                      <button onClick={() => removeFile(i)} style={{ background: "none", border: "none", cursor: "pointer", color: "#dc2626", padding: 0 }}>
                        <i className="bi bi-x" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {error && <div style={{ padding: "9px 13px", borderRadius: 9, background: "#fef2f2", border: "1.5px solid #fecaca", color: "#991b1b", fontSize: "0.82rem" }}>{error}</div>}
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: isNarrow ? "12px 16px" : "12px 20px", borderTop: "1.5px solid #f1f5f9", background: "#fafafa", display: "flex", gap: 8, flexShrink: 0 }}>
          <button onClick={onClose} style={{ flex: 1, background: "#fff", border: "1.5px solid #e5e7eb", borderRadius: 10, padding: "10px 0", fontWeight: 700, fontSize: "0.86rem", color: "#374151", cursor: "pointer" }}>
            İptal
          </button>
          <button onClick={handleSubmit} disabled={saving}
            style={{ flex: 2, background: "linear-gradient(135deg,#1d4ed8,#2563eb)", border: "none", borderRadius: 10, padding: "10px 0", fontWeight: 700, fontSize: "0.86rem", color: "#fff", cursor: saving ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 7, boxShadow: "0 3px 12px rgba(29,78,216,0.3)" }}>
            {saving ? <><span className="spinner-border spinner-border-sm" /> Kaydediliyor…</> : <><i className="bi bi-check-lg" /> Fatura Oluştur</>}
          </button>
        </div>
      </div>

      {showVendorManager && (
        <VendorManagerModal onClose={() => setShowVendorManager(false)} />
      )}
    </>
  );
}
